import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      background: transparent;
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['MinIO', 'RabbitMQ', 'Pytorch', 'Hadoop', 'Thrift', 'Kubernetes', 'AWS', 'Node.js', 'Docker', 'Folly'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! I am Kaushal, a software engineer at {' '}
              <a href="https://www.rubrik.com/" target="_blank" rel="noreferrer">
                Rubrik
              </a>
              . I completed my Masters of Science (MS) in Computer Science from the{' '}
              <a href="https://www.cs.wisc.edu/" target="_blank" rel="noreferrer">
                University of Wisconsin-Madison
              </a>{' '}
              focusing on Machine Learning. My course work includes subjects like Advanced Natural
              Language Processing, Deep Learning for Visual recognition, Machine Learning and Matrix
              Method in Machine Learning.
            </p>
            <p>
              I completed my Bachelors in Computer Engineering from{' '}
              <a href="http://www.nsit.ac.in/">Netaji Subhas Institute of Technology, New Delhi</a>{' '}
              in 2019 where I was awarded merit scholarship for academic excellence during my
              undergraduate studies. During my undergraduate days I got a good understanding of
              subjects like Operating Systems, Database Management Systems, Compilers, Computer
              Networks, Computer Graphics to name a few.
            </p>

            <p>
              Before pursuing my Masters I was working as an Analyst for{' '}
              <a href="https://www.goldmansachs.com/">Goldman Sachs </a> under their compliance
              division. Prior to that I have also worked as a full-time Software Engineer at{' '}
              <a href="https://research.samsung.com/"> Samsung Research </a> and as a summer intern
              at <a href="https://www.amazon.com/"> Amazon </a>.
            </p>
            <p>
              During the summer of 2022, I worked as a Software Engineer Intern at{' '}
              <a href="https://about.facebook.com/meta/">Meta (Facebook) </a> at their Menlo Park
              Campus.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
